import React from 'react';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import { AddRounded, DeleteRounded, EditRounded } from '@mui/icons-material';
import Calendar, { CalendarEvent } from 'components/Calendar';
import { HomeEvent, monthKey } from '../../api/events';
import {
	ActionButton,
	AddButton,
	AnnouncementHeader,
	EntryActions,
	HomePageContainer,
	Section,
	SectionHeader,
	SectionHeading,
	SectionTitle,
	SectionSubtitle,
	AnnouncementCard,
	AnnouncementTitle,
	AnnouncementDate,
	AnnouncementDescription,
	CalendarContainer,
	EventCard,
	EventDate,
	EventDay,
	EventMonth,
	EventInfo,
	EventTitle,
	EventDescription,
} from './styles';
import { Announcement } from 'types/announcements';

interface HomePageViewProps {
	announcements: Announcement[];
	isLoadingAnnouncements: boolean;
	announcementsError: string | null;
	events: HomeEvent[];
	isLoadingEvents: boolean;
	eventsError: string | null;
	visibleMonth: string;
	canManage: boolean;
	onMonthChange: (month: Date) => void;
	onAddAnnouncement: () => void;
	onAddEvent: () => void;
	onEditAnnouncement: (announcement: Announcement) => void;
	onDeleteAnnouncement: (announcement: Announcement) => void;
	onEditEvent: (event: HomeEvent) => void;
	onDeleteEvent: (event: HomeEvent) => void;
}

const HomePageView = ({
	announcements,
	isLoadingAnnouncements,
	announcementsError,
	events,
	isLoadingEvents,
	eventsError,
	visibleMonth,
	canManage,
	onMonthChange,
	onAddAnnouncement,
	onAddEvent,
	onEditAnnouncement,
	onDeleteAnnouncement,
	onEditEvent,
	onDeleteEvent,
}: HomePageViewProps) => {
	const monthEvents = events.filter((event) => monthKey(event.date) === visibleMonth);
	const calendarEvents: CalendarEvent[] = monthEvents.map((event) => ({
		id: event.id,
		date: event.date,
		title: event.title,
		description: event.description,
		color: event.color,
	}));

	return (
		<HomePageContainer>
			{/* Anuncios Section */}
			<Section>
				<SectionHeader>
					<SectionHeading>
						<SectionTitle>Anuncios</SectionTitle>
						<SectionSubtitle>Últimas Noticias</SectionSubtitle>
					</SectionHeading>
					{canManage && (
						<AddButton
							variant='contained'
							color='primary'
							startIcon={<AddRounded />}
							onClick={onAddAnnouncement}
						>
							Añadir anuncio
						</AddButton>
					)}
				</SectionHeader>

				{announcementsError && (
					<Typography color='error' variant='body2'>
						{announcementsError}
					</Typography>
				)}

				{isLoadingAnnouncements ? (
					<>
						<Skeleton variant='rounded' width='100%' height={150} />
						<Skeleton variant='rounded' width='100%' height={150} />
					</>
				) : announcements.length === 0 && !announcementsError ? (
					<Typography color='text.secondary' variant='body2'>
						No hay anuncios.
					</Typography>
				) : (
					announcements.map((announcement) => (
						<AnnouncementCard key={announcement.id}>
							<AnnouncementHeader>
								<div>
									<AnnouncementTitle>{announcement.title}</AnnouncementTitle>
									<AnnouncementDate>{announcement.date}</AnnouncementDate>
								</div>
								{canManage && (
									<EntryActions>
										<Tooltip title='Editar' arrow>
											<ActionButton size='small' onClick={() => onEditAnnouncement(announcement)}>
												<EditRounded fontSize='small' />
											</ActionButton>
										</Tooltip>
										<Tooltip title='Eliminar' arrow>
											<ActionButton
												size='small'
												color='error'
												onClick={() => onDeleteAnnouncement(announcement)}
											>
												<DeleteRounded fontSize='small' />
											</ActionButton>
										</Tooltip>
									</EntryActions>
								)}
							</AnnouncementHeader>
							<AnnouncementDescription>
								{announcement.description}
							</AnnouncementDescription>
						</AnnouncementCard>
					))
				)}
			</Section>

			{/* Próximos Eventos Section */}
			<Section>
				<SectionHeader>
					<SectionHeading>
						<SectionTitle>Próximos Eventos</SectionTitle>
						<SectionSubtitle>Fechas importantes este mes</SectionSubtitle>
					</SectionHeading>
					{canManage && (
						<AddButton
							variant='contained'
							color='primary'
							startIcon={<AddRounded />}
							onClick={onAddEvent}
						>
							Añadir evento
						</AddButton>
					)}
				</SectionHeader>

				{/* Calendar */}
				<CalendarContainer>
					<Calendar
						events={calendarEvents}
						maxEventDots={3}
						month={visibleMonth}
						onMonthChange={onMonthChange}
					/>
				</CalendarContainer>

				{eventsError && (
					<Typography color='error' variant='body2'>
						{eventsError}
					</Typography>
				)}

				{isLoadingEvents ? (
					<>
						<Skeleton variant='rounded' width='100%' height={72} />
						<Skeleton variant='rounded' width='100%' height={72} />
					</>
				) : monthEvents.length === 0 && !eventsError ? (
					<Typography color='text.secondary' variant='body2'>
						No hay eventos este mes.
					</Typography>
				) : (
					monthEvents.map((event) => (
						<EventCard key={event.id}>
							<EventDate>
								<EventDay>{event.day}</EventDay>
								<EventMonth>{event.month}</EventMonth>
							</EventDate>
						<EventInfo>
							<EventTitle>{event.title}</EventTitle>
							<EventDescription>{event.description}</EventDescription>
						</EventInfo>
						{canManage && (
							<EntryActions>
								<Tooltip title='Editar' arrow>
									<ActionButton size='small' onClick={() => onEditEvent(event)}>
										<EditRounded fontSize='small' />
									</ActionButton>
								</Tooltip>
								<Tooltip title='Eliminar' arrow>
									<ActionButton size='small' color='error' onClick={() => onDeleteEvent(event)}>
										<DeleteRounded fontSize='small' />
									</ActionButton>
								</Tooltip>
							</EntryActions>
						)}
					</EventCard>
					))
				)}
			</Section>
		</HomePageContainer>
	);
};

export default HomePageView;
